import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

interface ProjectFormState {
  isMinimized: boolean;
  formData: any;
  hasUnsavedChanges: boolean;
  shouldOpenModal: boolean;
}

interface ProjectFormContextType {
  formState: ProjectFormState | null;
  minimizeForm: (data: any) => void;
  restoreForm: () => void;
  clearForm: () => void;
  hasMinimizedForm: boolean;
  acknowledgeModalOpen: () => void;
}

const ProjectFormContext = createContext<ProjectFormContextType | undefined>(undefined);

/**
 * Sanitizes form data to remove any potential cross-origin window references
 * and handles circular references to prevent infinite loops.
 * This is necessary when running in an iframe (harness) environment where
 * React's development mode logging can fail when encountering cross-origin objects.
 * 
 * The function creates a clean copy of the data by:
 * 1. Preserving Date objects (which JSON.stringify would convert to strings)
 * 2. Preserving arrays and plain objects
 * 3. Preserving primitive values
 * 4. Filtering out any objects that might have cross-origin references (Window, Event, etc.)
 * 5. Using a WeakSet to track visited objects and prevent infinite loops from circular references
 */
function sanitizeFormData(data: any, visited: WeakSet<object> = new WeakSet()): any {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data !== 'object') {
    return data;
  }

  if (visited.has(data)) {
    return undefined;
  }

  if (data instanceof Date) {
    return new Date(data.getTime());
  }

  visited.add(data);

  if (Array.isArray(data)) {
    return data.map(item => sanitizeFormData(item, visited));
  }

  try {
    if (typeof window !== 'undefined' && data === window) {
      return undefined;
    }
    if (data.constructor && data.constructor.name === 'Window') {
      return undefined;
    }
    if (data instanceof Event) {
      return undefined;
    }
    
    const sanitized: Record<string, any> = {};
    for (const key of Object.keys(data)) {
      try {
        const value = data[key];
        if (typeof value !== 'function') {
          sanitized[key] = sanitizeFormData(value, visited);
        }
      } catch {
        continue;
      }
    }
    return sanitized;
  } catch {
    return undefined;
  }
}

export function ProjectFormProvider({ children }: { children: ReactNode }) {
  const [formState, setFormState] = useState<ProjectFormState | null>(null);

  const minimizeForm = useCallback((data: any) => {
    const sanitizedData = sanitizeFormData(data);
    setFormState({
      isMinimized: true,
      formData: sanitizedData,
      hasUnsavedChanges: true,
      shouldOpenModal: false,
    });
  }, []);

  const restoreForm = () => {
    if (formState) {
      setFormState({
        ...formState,
        isMinimized: false,
        shouldOpenModal: true,
      });
    }
  };

  const clearForm = () => {
    setFormState(null);
  };

  const acknowledgeModalOpen = () => {
    if (formState) {
      setFormState({
        ...formState,
        shouldOpenModal: false,
      });
    }
  };

  const hasMinimizedForm = formState?.isMinimized === true;

  return (
    <ProjectFormContext.Provider
      value={{
        formState,
        minimizeForm,
        restoreForm,
        clearForm,
        hasMinimizedForm,
        acknowledgeModalOpen,
      }}
    >
      {children}
    </ProjectFormContext.Provider>
  );
}

export function useProjectForm() {
  const context = useContext(ProjectFormContext);
  if (context === undefined) {
    throw new Error('useProjectForm must be used within a ProjectFormProvider');
  }
  return context;
}
